import type {
  SendMfaCodeProps,
  SendMfaCodeResponse,
  VerifyMfaCodeProps,
  VerifyMfaCodeResponse,
} from '@iheartjane/data-access';
import { MfaProvider } from '@iheartjane/menu';
import type { PropsWithChildren } from 'react';
import { useCallback } from 'react';

import { useFetcherWithMutation } from '@jane/bloom/hooks/useFetcherWithMutation';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { isJaneUser } from '@jane/bloom/types/schemas/marketplace/janeUser';

// action errors arrive as `unknown`; pull a `message` field if present
const errorMessage = (error: unknown, fallback: string): string => {
  const message =
    error && typeof error === 'object' && 'message' in error
      ? (error as Record<string, unknown>)['message']
      : undefined;

  return message ? String(message) : fallback;
};

export const BloomMfaProvider = ({ children }: PropsWithChildren) => {
  const { whoami } = useRootLoaderData();
  const userId = isJaneUser(whoami.user) ? whoami.user.id : null;

  // only newPhone/code reach the routes — isKiosk/jwt are handled server-side
  const { mutate: sendCodeMutation } = useFetcherWithMutation<
    { newPhone?: string },
    SendMfaCodeResponse
  >({
    action: `/api/mfa/send-code`,
  });

  const { mutate: verifyCodeMutation } = useFetcherWithMutation<
    { code: string },
    VerifyMfaCodeResponse
  >({
    action: `/api/mfa/verify-code`,
  });

  // bridge the callback-based mutate to the Promise the MfaProvider expects.
  // send rejects on failure so the provider surfaces `errorSendingCode`.
  const sendMfaCode = useCallback(
    ({ newPhone }: SendMfaCodeProps): Promise<SendMfaCodeResponse> =>
      new Promise((resolve, reject) => {
        sendCodeMutation(
          { newPhone },
          {
            onError: (error) =>
              reject(new Error(errorMessage(error, 'Failed to send code'))),
            onSuccess: resolve,
          }
        );
      }),
    [sendCodeMutation]
  );

  // verify never rejects — a wrong code is a normal `{ success: false }` result
  const verifyMfaCode = useCallback(
    ({ code }: VerifyMfaCodeProps): Promise<VerifyMfaCodeResponse> =>
      new Promise((resolve) => {
        verifyCodeMutation(
          { code },
          {
            onError: (error) =>
              resolve({
                error: errorMessage(error, 'Something went wrong'),
                success: false,
              }),
            onSuccess: resolve,
          }
        );
      }),
    [verifyCodeMutation]
  );

  return (
    <MfaProvider
      getJwt={() => null}
      sendMfaCode={sendMfaCode}
      userId={userId}
      verifyMfaCode={verifyMfaCode}
    >
      {children}
    </MfaProvider>
  );
};
